import React from 'react'
import { X } from 'lucide-react'
import { Company } from '@/types/company'
import { ADMIN_MENU_PERMISSIONS, ALL_ADMIN_MENU_KEYS } from '@/lib/adminPermissions'

interface AddAdminModalProps {
  company?: Company
  onClose: () => void
  onSubmit: (data: { name: string; email: string; password: string; permissions?: string[] }) => Promise<void>
}

export const AddAdminModal: React.FC<AddAdminModalProps> = ({
  company,
  onClose,
  onSubmit
}) => {
  // company prop'u SADECE bir firmaya yönetici eklerken geçilir (CompanyTable/CompanyActions).
  // cloud-super-admin'in kendi "Kullanıcı Yönetimi" ekranından yeni bir super_admin eklenirken
  // company geçilmez - checkbox'lar sadece bu durumda gösterilir.
  const isSuperAdminCreation = !company

  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    password: ''
  })
  const [checkedPermissions, setCheckedPermissions] = React.useState<Set<string>>(
    new Set(ALL_ADMIN_MENU_KEYS)
  )

  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const togglePermission = (key: string) => {
    setCheckedPermissions(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    try {
      setIsSubmitting(true)
      const payload = isSuperAdminCreation
        ? {
            ...formData,
            // Hepsi işaretliyse boş dizi gönder (tam yetki - admin@admin.com ile aynı anlam)
            permissions: checkedPermissions.size === ALL_ADMIN_MENU_KEYS.length
              ? []
              : Array.from(checkedPermissions)
          }
        : formData
      await onSubmit(payload)
      onClose()
    } catch (error) {
      console.error('Form gönderilirken hata:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50">
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-card rounded-xl shadow-lg border border-border p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            {company ? `${company.name} - Yönetici Ekle` : 'Yeni Yönetici Ekle'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">İsim</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Yönetici adını girin"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">E-posta</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="E-posta adresini girin"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Şifre</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder="Şifre belirleyin"
              required
            />
          </div>

          {isSuperAdminCreation && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Panel Erişimi (varsayılan: tümü)
              </label>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3 rounded-lg border border-border bg-muted/30 max-h-56 overflow-y-auto">
                {ADMIN_MENU_PERMISSIONS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checkedPermissions.has(key)}
                      onChange={() => togglePermission(key)}
                      className="rounded border-border"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
              disabled={isSubmitting}
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
